<div class="container-fluid">
  <hc-page-heading [description]="'profile.subtitle' | translate">
    {{ 'profile.my_profile' | translate }}
  </hc-page-heading>

  <div class="row g-3">
    <!-- User Info Card (Readonly) -->
    <div class="col-xl-4">
      <div class="card card-v2 h-100">
        <div class="card-body">
          <div class="d-flex align-items-center mb-4">
            <span class="avatar-initial avatar-lg avatar-{{ (user?.profile?.first_name || user?.name || 'U')[0] | lowercase }}">
              {{ (user?.profile?.first_name || user?.name || 'U')[0] | uppercase }}{{ (user?.profile?.last_name || '')[0] | uppercase }}
            </span>
            <div>
              <h5 class="mb-1 font-semibold">
                {{ user?.name || (user?.profile?.first_name + ' ' + user?.profile?.last_name) }}
              </h5>
              <hc-badge color="neutral" variant="light" size="small">{{ getUserRole() }}</hc-badge>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label-v2 text-muted">{{ 'profile.email' | translate }}</label>
            <p class="mb-0 text-sm">{{ user?.email }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label-v2 text-muted">{{ 'profile.last_login' | translate }}</label>
            <p class="mb-0 text-sm">{{ getLastLogin() }}</p>
          </div>

          @if (assignedHotels.length > 0) {
            <div class="mb-3">
              <label class="form-label-v2 text-muted">{{ 'profile.assigned_hotels' | translate }}</label>
              <ul class="list-unstyled mb-0">
                @for (hotel of assignedHotels; track hotel) {
                  <li class="mb-1 text-sm">
                    <lucide-icon name="building-2" class="me-1 text-primary"></lucide-icon> {{ hotel.name }}
                  </li>
                }
              </ul>
            </div>
          }

          <!-- Two-Factor Authentication Section -->
          <div class="two-factor-section">
            <app-two-factor-settings
              [initiallyEnabled]="isTwoFactorEnabled"
              (statusChange)="onTwoFactorStatusChange($event)">
            </app-two-factor-settings>

            <!-- Trusted Devices Section (Only show if 2FA is enabled) -->
            @if (isTwoFactorEnabled) {
              <hr class="mt-3 mb-3">
              <div class="mt-3">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0">
                    <div class="trusted-devices-section-icon">
                      <lucide-icon name="monitor-smartphone"></lucide-icon>
                    </div>
                    {{ 'profile.trusted_devices.title' | translate }}
                  </h6>
                  @if (trustedDevices.length > 0) {
                    <hc-button
                      variant="destructive-secondary"
                      leftIcon="trash-2"
                      (clicked)="revokeAllDevices()"
                      [disabled]="isLoadingDevices">
                      {{ 'profile.trusted_devices.revoke_all' | translate }}
                    </hc-button>
                  }
                </div>

                <p class="text-muted small mb-3">
                  {{ 'profile.trusted_devices.description' | translate }}
                </p>

                <!-- Loading State -->
                @if (isLoadingDevices) {
                  <div class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
                    </div>
                  </div>
                }

                <!-- Empty State -->
                @if (!isLoadingDevices && trustedDevices.length === 0) {
                  <div class="alert alert-info">
                    <lucide-icon name="info" class="me-2"></lucide-icon>
                    {{ 'profile.trusted_devices.no_devices' | translate }}
                  </div>
                }

                <!-- Trusted Devices List -->
                @if (!isLoadingDevices && trustedDevices.length > 0) {
                  <div class="trusted-devices-list">
                    @for (device of trustedDevices; track device.id) {
                      <div class="trusted-device-card card mb-3">
                        <div class="card-body">

                          <!-- Row 1: Icon + Device name, IP, last used -->
                          <div class="td-row-top">
                            <div class="td-icon">
                              <lucide-icon [name]="getDeviceIcon(device.device_name)" class="fs-2 text-primary"></lucide-icon>
                            </div>
                            <div class="td-meta">
                              <h6 class="td-name">{{ device.device_name }}</h6>
                              <div class="td-details text-muted small">
                                @if (device.ip_address) {
                                  <span class="td-detail-item">
                                    <lucide-icon name="network"></lucide-icon>
                                    {{ device.ip_address }}
                                  </span>
                                }
                                <span class="td-detail-item">
                                  <lucide-icon name="clock"></lucide-icon>
                                  {{ 'profile.trusted_devices.last_used' | translate }}:
                                  <strong>{{ getRelativeTime(device.last_used_at) }}</strong>
                                </span>
                              </div>
                            </div>
                          </div>

                          <!-- Row 2: Badge + Revoke button -->
                          <div class="td-row-bottom">
                            <div class="td-badge-group">
                              @if (isExpiringSoon(device.expires_at)) {
                                <hc-badge color="warning" size="small" variant="light" [dispIcon]="true">
                                  <lucide-icon badgeIcon name="triangle-alert"></lucide-icon>
                                  {{ 'profile.trusted_devices.expires_in' | translate }}
                                  {{ getDaysUntilExpiry(device.expires_at) }}
                                  {{ getDaysUntilExpiry(device.expires_at) === 1 ?
                                  ('profile.trusted_devices.day' | translate) :
                                  ('profile.trusted_devices.days' | translate) }}
                                </hc-badge>
                              } @else {
                                <hc-badge color="success" size="small" variant="light" [dispIcon]="true">
                                  <lucide-icon badgeIcon name="circle-check"></lucide-icon>
                                  {{ 'profile.trusted_devices.active' | translate }}
                                  ({{ getDaysUntilExpiry(device.expires_at) }}
                                  {{ getDaysUntilExpiry(device.expires_at) === 1 ?
                                  ('profile.trusted_devices.day' | translate) :
                                  ('profile.trusted_devices.days' | translate) }}
                                  {{ 'profile.trusted_devices.left' | translate }})
                                </hc-badge>
                              }
                              <span class="td-added text-muted small">
                                {{ 'profile.trusted_devices.added' | translate | uppercase }}:
                                {{ formatDeviceDate(device.created_at) | uppercase }}
                              </span>
                            </div>
                            <hc-button
                              variant="destructive-secondary"
                              leftIcon="circle-x"
                              (clicked)="openRevokeDeviceModal(device)"
                              [ariaLabel]="'profile.trusted_devices.revoke_device' | translate">
                              {{ 'profile.trusted_devices.revoke' | translate }}
                            </hc-button>
                          </div>

                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- Revoke Device Confirmation Modal -->
            @if (showRevokeDeviceModal && deviceToRevoke) {
              <div class="modal fade show d-block revoke-device-modal-backdrop" tabindex="-1" role="dialog">
                <div class="modal-dialog modal-dialog-centered" role="document">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title">
                        <lucide-icon name="circle-alert" class="text-warning me-2"></lucide-icon>
                        {{ 'profile.trusted_devices.revoke_confirm_title' | translate }}
                      </h5>
                      <button
                        type="button"
                        class="btn-close"
                        (click)="closeRevokeDeviceModal()"
                        [disabled]="isRevokingDevice"
                        aria-label="Close"
                      ></button>
                    </div>
                    <div class="modal-body">
                      <p>{{ 'profile.trusted_devices.revoke_confirm_message' | translate }}</p>

                      <div class="device-info-box p-3 bg-light rounded">
                        <div class="d-flex align-items-center">
                          <lucide-icon [name]="getDeviceIcon(deviceToRevoke.device_name)" class="fs-3 text-primary me-3"></lucide-icon>
                          <div>
                            <div class="fw-bold">{{ deviceToRevoke.device_name }}</div>
                            @if (deviceToRevoke.ip_address) {
                              <div class="text-muted small">{{ deviceToRevoke.ip_address }}</div>
                            }
                            <div class="text-muted small">
                              {{ 'profile.trusted_devices.last_used' | translate }}:
                              {{ getRelativeTime(deviceToRevoke.last_used_at) }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="alert alert-info mt-3 mb-0">
                        <lucide-icon name="info" class="me-2"></lucide-icon>
                        {{ 'profile.trusted_devices.revoke_note' | translate }}
                      </div>

                      @if (revokeDeviceError) {
                        <div class="alert alert-danger mt-3 mb-0">
                          {{ revokeDeviceError }}
                        </div>
                      }
                    </div>
                    <div class="modal-footer">
                      <hc-button
                        variant="secondary"
                        (clicked)="closeRevokeDeviceModal()"
                        [disabled]="isRevokingDevice">
                        {{ 'common.cancel' | translate }}
                      </hc-button>
                      <hc-button
                        variant="danger"
                        (clicked)="confirmRevokeDevice()"
                        [disabled]="isRevokingDevice"
                        [loading]="isRevokingDevice">
                        {{ 'profile.trusted_devices.revoke_device' | translate }}
                      </hc-button>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Profile & Change Password -->
    <div class="col-xl-8">
      <!-- Edit Profile Card -->
      <div class="card card-v2 mb-3">
        <div class="card-body">
          <!-- Form Section Header -->
          <div class="form-section">
            <div class="form-section-header">
              <div class="form-section-icon">
                <lucide-icon name="user-pen"></lucide-icon>
              </div>
              <h5 class="form-section-title">{{ 'profile.edit_profile' | translate }}</h5>
            </div>

            <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <hc-form-field
                    [label]="'profile.first_name' | translate"
                    [required]="true"
                    [error]="(f['firstName'].touched || profileSubmitted) && f['firstName'].errors
                      ? (f['firstName'].errors['required']
                        ? ('validations.first_name' | translate)
                        : f['firstName'].errors['minlength']
                          ? ('VALIDATION.MINLENGTH' | translate: {length: 2})
                          : ('VALIDATION.MAXLENGTH' | translate: {length: 30}))
                      : ''">
                    <hc-input type="text" formControlName="firstName"
                      [placeholder]="'profile.first_name_placeholder' | translate"
                      [hasError]="!!((f['firstName'].touched || profileSubmitted) && f['firstName'].errors)">
                    </hc-input>
                  </hc-form-field>
                </div>
                <div class="col-md-6 mb-3">
                  <hc-form-field
                    [label]="'profile.last_name' | translate"
                    [required]="true"
                    [error]="(f['lastName'].touched || profileSubmitted) && f['lastName'].errors
                      ? (f['lastName'].errors['required']
                        ? ('validations.last_name' | translate)
                        : f['lastName'].errors['minlength']
                          ? ('VALIDATION.MINLENGTH' | translate: {length: 2})
                          : ('VALIDATION.MAXLENGTH' | translate: {length: 30}))
                      : ''">
                    <hc-input type="text" formControlName="lastName"
                      [placeholder]="'profile.last_name_placeholder' | translate"
                      [hasError]="!!((f['lastName'].touched || profileSubmitted) && f['lastName'].errors)">
                    </hc-input>
                  </hc-form-field>
                </div>
              </div>

              <div class="d-flex gap-2 justify-content-end mt-2">
                <hc-button variant="secondary" size="md" (clicked)="onCancel()" class="mr-2">{{'cancel' | translate }}</hc-button>
                <hc-button
                  variant="primary"
                  type="submit"
                  size="md"
                  [disabled]="profileForm.invalid || isSavingProfile">
                  @if (isSavingProfile) {
                    <span class="spinner-border spinner-border-sm me-1"></span>
                  }
                  {{ 'save' | translate }}
                </hc-button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Change Password Card -->
      <div class="card card-v2">
        <div class="card-body">
          <div class="form-section">
            <div class="form-section-header">
              <div class="form-section-icon">
                <lucide-icon name="key-round"></lucide-icon>
              </div>
              <h5 class="form-section-title">{{ 'profile.change_password' | translate }}</h5>
            </div>

            <form [formGroup]="passwordForm" (ngSubmit)="changePassword()">
              <div class="row">
                <div class="col-md-12 mb-3">
                  <hc-form-field
                    [label]="'profile.current_password' | translate"
                    [required]="true"
                    [error]="(pf['currentPassword'].touched || passwordSubmitted) && pf['currentPassword'].errors?.['required']
                      ? ('profile.current_password_required' | translate)
                      : ''">
                    <hc-input type="password"
                      formControlName="currentPassword"
                      autocomplete="current-password"
                      [placeholder]="'profile.current_password_placeholder' | translate"
                      [hasError]="!!((pf['currentPassword'].touched || passwordSubmitted) && pf['currentPassword'].errors)">
                    </hc-input>
                  </hc-form-field>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <hc-form-field
                    [label]="'profile.new_password' | translate"
                    [required]="true"
                    [error]="(pf['newPassword'].touched || passwordSubmitted) && pf['newPassword'].errors
                      ? (pf['newPassword'].errors['required']
                        ? ('profile.new_password_required' | translate)
                        : pf['newPassword'].errors['passwordTooShort']
                          ? ('password_hints.error_too_short' | translate)
                          : pf['newPassword'].errors['passwordNoLetter']
                            ? ('password_hints.error_no_letter' | translate)
                            : ('password_hints.error_no_number' | translate))
                      : ''">
                    <hc-input type="password"
                      formControlName="newPassword"
                      autocomplete="new-password"
                      [placeholder]="'profile.new_password_placeholder' | translate"
                      [hasError]="!!((pf['newPassword'].touched || passwordSubmitted) && pf['newPassword'].errors)">
                    </hc-input>
                  </hc-form-field>
                  <app-password-hints [password]="pf['newPassword'].value"></app-password-hints>
                </div>
                <div class="col-md-6 mb-3">
                  <hc-form-field
                    [label]="'profile.confirm_password' | translate"
                    [required]="true"
                    [error]="(pf['confirmPassword'].touched || passwordSubmitted) && pf['confirmPassword'].errors?.['required']
                      ? ('profile.confirm_password_required' | translate)
                      : (passwordForm.errors?.['mismatch'] && (pf['confirmPassword'].touched || passwordSubmitted))
                        ? ('AUTH.RESET_PASSWORD.PASSWORDS_DONT_MATCH' | translate)
                        : ''">
                    <hc-input type="password"
                      formControlName="confirmPassword"
                      autocomplete="new-password"
                      [placeholder]="'profile.confirm_password_placeholder' | translate"
                      [hasError]="!!((pf['confirmPassword'].touched || passwordSubmitted) && (pf['confirmPassword'].errors || passwordForm.errors?.['mismatch']))">
                    </hc-input>
                  </hc-form-field>
                </div>
              </div>


              <div class="d-flex justify-content-end">
                <hc-button
                  variant="primary"
                  type="submit"
                  size="md"
                  [disabled]="isChangingPassword">
                  @if (isChangingPassword) {
                    <span class="spinner-border spinner-border-sm me-1"></span>
                  }
                  {{ 'profile.change_password' | translate }}
                </hc-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
